import {
  SkyDialog,
  SkyInput,
  SkyMessageBar,
  SkyText,
  SkyTextarea,
} from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { TenantView } from "@shared/types/tenants";
import { ErrorState } from "./ScreenState";

/**
 * The dialog's promise, word for word (owner decision 2026-10-10, F32 PR3; reworded by the owner
 * 2026-10-13, decision 6B, to match the Tenants callout in plain words, #929).
 */
export const DELETE_TENANT_PROMISE =
  "Deleting a tenant stops all its work at once. Its stored data is removed later by Skypoint, following the offboarding steps.";

/**
 * Delete a DISABLED tenant (F32 PR3, owner decision 2026-10-10). Shown only for a disabled
 * tenant: the server refuses an active one (disable first). The person types the tenant's Name
 * (fixed, shown in the Name column) and a reason; the delete is final in skyData. Nothing in Azure,
 * in the tenant database or in Snowflake is removed: skyData hands Infra an offboarding list, which
 * the drawer shows once the delete is done.
 */
export function DeleteTenantDialog({
  open,
  onOpenChange,
  tenant,
  onConfirm,
  isSaving,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: TenantView | null;
  onConfirm: (body: { confirmName: string; reason: string }) => void;
  isSaving: boolean;
  error: unknown;
}) {
  // Empty on every opening: the caller remounts it with a key.
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [asked, setAsked] = useState(false);

  if (!tenant) return null;
  const nameMatches = typed.trim() === tenant.name;
  const trimmedReason = reason.trim();

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Delete ${tenant.displayName}`}
      description={DELETE_TENANT_PROMISE}
      size="medium"
      testId="tenant-delete-dialog"
      // Closes only when the call succeeds, so a refusal leaves the typing where it is.
      onBeforeClose={(reasonToClose) => reasonToClose !== "primaryAction"}
      primaryAction={{
        label: "Delete tenant",
        onClick: () => {
          setAsked(true);
          if (!nameMatches || trimmedReason === "") return;
          onConfirm({ confirmName: typed.trim(), reason: trimmedReason });
        },
        loading: isSaving,
        destructive: true,
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        {error ? <ErrorState title="The tenant was not deleted" error={error} /> : null}

        <SkyMessageBar
          intent="error"
          layout="multiline"
          title="This cannot be undone in skyData"
          message="The tenant cannot be enabled again. Nobody can open its instances, and nothing runs for it. skyData then lists every resource it left behind for the Infra team."
          testId="tenant-delete-final"
        />

        <SkyInput
          label={`Type the tenant's name, ${tenant.name}, to confirm`}
          required
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          validationState={asked && !nameMatches ? "error" : "none"}
          validationMessage={
            asked && !nameMatches
              ? "The name you typed does not match this tenant's name."
              : undefined
          }
          autoComplete="off"
          testId="tenant-delete-name"
        />

        <SkyTextarea
          label="Reason"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="Recorded on the audit event beside your name."
          validationState={asked && trimmedReason === "" ? "error" : "none"}
          validationMessage={
            asked && trimmedReason === "" ? "Say why the tenant is being deleted." : undefined
          }
          maxLength={500}
          rows={3}
          testId="tenant-delete-reason"
        />

        <SkyText as="p" size={200} color="muted" block>
          The delete is recorded on the audit trail with your name and the reference id of this
          request.
        </SkyText>
      </div>
    </SkyDialog>
  );
}
